import React, { useEffect, useId, useRef, useState } from "react";
import { contentFadeFor } from "../round-over/RoundOverCard";
import { useSoundOn } from "../../../audio/use-sound";
import { NO_POWERUPS_MODE } from "core/src/playstyles.js";
import { wormColors } from "core/src/core/constants.js";
import { flagUrlSquare, isFlagCode } from "../../../shared/flags";
import { modeLook, ordinal, SIZE_LABEL } from "../../../online/dynamic";
import type { ArenaShape, DynamicKind, SizeKey } from "../../../online/dynamic";
import s from "./NextRoundReveal.module.scss";

// The announcement of the next round, in six designs to compare.
//   slam    the proud full-screen one (sketch D70): a skewed panel in the mode's
//           colour wipes across, the name slams in, then its line and the map
// The other five leave the board in view (the survivors' lap and the score are
// on it during this pause) and never show the map:
//   banner  a slim band across the upper part, slides in and out (the default;
//           it holds a second longer and ends just after the round change)
//   chip    a small card top centre, under the game's top line
//   corner  a ticket top right, under the buttons, with the time left as a line
//   stamp   the name as big letters over the middle of the board, no panel; the
//           board dims a little while it holds and the letters carry a dark
//           halo, so they read as in front of the trails. Five takes:
//           stamp (outlined), stamp2 (the outline draws itself), stamp3 (the
//           icon huge and faint behind), stamp4 (two lines, set at an angle),
//           stamp5 (letter by letter), stamp6 (stamp4's two angled lines in
//           stamp's outlined letters)
//   edge    the screen's edge lights up in the mode's colour, a tab on the top edge
//   board1-5  the standings as a proper list (rank, flag, name, a bar for the
//           score, the round's gain) with the next round attached: a centre
//           card (1), the next round as a coloured header (2), a column on the
//           left (3), a wide low two-column list (4), big bars only (5)
//   pass    a band in the mode's colour comes in from the left, stays over the
//           middle while the board behind it becomes the next round's, and
//           leaves to the right (see useStraddlingReveal: it outlives the pause)
// Played once when mounted, gone by `durationMs`. No countdown here: the game
// shows its own right after.
export type RevealVariant = "slam" | "banner" | "chip" | "corner" | "stamp" | "stamp2" | "stamp3" | "stamp4" | "stamp5" | "stamp6" | "edge" | "pass" | "board1" | "board2" | "board3" | "board4" | "board5";
export const REVEAL_VARIANTS: RevealVariant[] = ["slam", "banner", "chip", "corner", "stamp", "stamp4", "stamp6", "stamp2", "stamp3", "stamp5", "edge", "pass", "board1", "board2", "board3", "board4", "board5"];
export const isStamp = (v: RevealVariant) => v.indexOf("stamp") === 0;
export const isBoard = (v: RevealVariant) => v.indexOf("board") === 0;
// The designs that are the pause's one screen: what the round meant first (the
// standings, who is out), then the next round joins it.
export const revealHasResult = (v: RevealVariant) => v === "banner" || isBoard(v);
const isVariant = (v: string | null): v is RevealVariant => !!v && (REVEAL_VARIANTS as string[]).indexOf(v) !== -1;

// Which design a real game plays: board1 (the standings in a compact card in the
// centre, the next round opening from its bottom edge), unless `?reveal=<name>` on any page picks another for
// the rest of the tab's session. Read once, when the app loads: /play-now and
// /royale rewrite their query string soon after.
const VARIANT_KEY = "ar.revealVariant";
function readVariant(): RevealVariant {
  try {
    const asked = new URLSearchParams(window.location.search).get("reveal");
    if (isVariant(asked)) { window.sessionStorage.setItem(VARIANT_KEY, asked); return asked; }
    const kept = window.sessionStorage.getItem(VARIANT_KEY);
    if (isVariant(kept)) return kept;
  } catch (e) { /* no storage: the default */ }
  return "board1";
}
const PICKED = readVariant();
export function revealVariant(): RevealVariant { return PICKED; }

export interface NextRoundRevealProps {
  mode: string;            // the round mode's name (online/dynamic.ts modeLook)
  shape: ArenaShape;       // the map: only the slam shows it
  sizeKey: SizeKey;
  context: DynamicKind;    // Play Now (teal accents) or Royale (gold)
  kicker?: string;         // small line above the name, e.g. "Round 4/7"
  line?: string;           // an extra line under the mode's own, e.g. "24 left · 3 go out this round"
  durationMs: number;      // how long the reveal may take; it must be gone by then
  variant?: RevealVariant; // the design; left out, the one picked with ?reveal=
  // The banner only. `result`: what the finished round meant (BannerStandings,
  // BannerOut), the band's first row; after `beatMs` the next round's row
  // slides down under it and both stay. `barMs`: the time until the next round
  // starts, as a line that drains.
  result?: React.ReactNode;
  standings?: Standings;   // the same, as data: the banner's row of entries, a board's list
  beatMs?: number;
  barMs?: number;
}

// The reveal at full length. With less time (2000 ms is the least that still
// reads well) the entrance and the hold are squeezed; it never outlasts durationMs.
export const REVEAL_MS = 3200;
// A design's own full length: the banner holds a second longer. The layers
// start the reveal this long (and a breath) before the pause ends.
export function revealMs(variant: RevealVariant): number {
  return variant === "pass" ? PASS_MS : revealHasResult(variant) ? REVEAL_MS + 1000 : REVEAL_MS;
}
// How long before the pause ends a design begins. Most are gone when the pause
// is; the pass-through and the banner begin later than their length and stay on
// into the next round's own 3, 2, 1 (the banner for half a second).
export function revealLeadMs(variant: RevealVariant): number {
  return variant === "pass" ? PASS_LEAD_MS : revealHasResult(variant) ? revealMs(variant) - 500 : revealMs(variant) + 200;
}
export const revealStraddles = (variant: RevealVariant) => revealLeadMs(variant) < revealMs(variant);
const MIN_MS = 900;
const OUT_MS = 480; // the wipe away
// The pass-through: in place this long before the pause ends, and this long
// into the new round's own 3, 2, 1 before it leaves.
export const PASS_LEAD_MS = 1200;
export const PASS_TAIL_MS = 1000;
export const PASS_MS = PASS_LEAD_MS + PASS_TAIL_MS + OUT_MS + 60;

// ---------- mode illustrations (48 grid, bold strokes) ----------
const ill = { viewBox: "0 0 48 48", fill: "none", stroke: "currentColor", strokeWidth: 4, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
const HEX48 = "24,4 41.3,14 41.3,34 24,44 6.7,34 6.7,14";
const BOLT = "M27 11l-10 15h7l-3 11 10-15h-7l3-11z";
const IClassic = () => (<svg {...ill}><polygon points={HEX48} fill="currentColor" fillOpacity="0.16" /><path d={BOLT} fill="currentColor" strokeWidth={2} /></svg>);
const IIdentity = () => (<svg {...ill}><path d="M9 17h27M29 9l8 8-8 8" /><path d="M39 31H12M19 23l-8 8 8 8" /></svg>);
const ISabotage = () => (<svg {...ill}><circle cx="21" cy="29" r="13" fill="currentColor" fillOpacity="0.2" /><path d="M30 20l5-5" /><path d="M40 4v4M46 11h-4M45 5l-3 3" strokeWidth={3} /><path d="M14 27a8 8 0 015-6" strokeWidth={3} /></svg>);
const IDrink = () => (<svg {...ill}><g transform="rotate(-10 20 24)"><path d="M6 9h26L19 25z" fill="currentColor" fillOpacity="0.2" /><path d="M19 25v14M11 39h16" /></g><path d="M40 12c4 4-4 7 0 11s-4 7 0 11" strokeWidth={3.5} /><circle cx="40" cy="40" r="2.6" fill="currentColor" stroke="none" /></svg>);
const IFatboy = () => (<svg {...ill}><path d="M9 35c9 0 7-19 16-19" strokeWidth={11} /><path d="M28 40c8 0 6-19 14-19" strokeWidth={2.6} /></svg>);
const INone = () => (<svg {...ill}><polygon points={HEX48} /><path d={BOLT} fill="currentColor" fillOpacity="0.3" stroke="none" /><path d="M9 39L39 9" strokeWidth={4.5} /></svg>);
const ICONS: Record<string, () => React.ReactElement> = {
  "Classic": IClassic,
  "Identity crisis": IIdentity,
  "Sabotage": ISabotage,
  "Drink and drive": IDrink,
  "Fatboy Slim": IFatboy,
  [NO_POWERUPS_MODE]: INone,
};
// A round mode's own bold icon (drawn in currentColor).
export function ModeIcon({ mode }: { mode: string | undefined | null }) {
  const Icon = (mode && ICONS[mode]) || IClassic;
  return <Icon />;
}

// The map as a small outline: a stadium pill or a rectangle.
export function MapShape({ shape, className }: { shape: ArenaShape | undefined | null; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 64 38" fill="none" aria-hidden="true">
      <rect x="2" y="2" width="60" height="34" rx={shape === "Rectangle" ? 3 : 17} fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeWidth="3" />
    </svg>
  );
}
const Crown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8l4 3.5L12 5l4 6.5L20 8l-1.6 10H5.6L4 8z" fill="currentColor" fillOpacity="0.25" /><path d="M6 21h12" />
  </svg>
);

export default function NextRoundReveal({ mode, shape, sizeKey, context, kicker, line, durationMs, variant, result: resultNode, standings, beatMs, barMs }: NextRoundRevealProps) {
  // The length is fixed at mount: a re-render with another durationMs must not
  // restart or stretch a reveal that is already playing.
  const v = variant || PICKED;
  const result = resultNode || (standings && v === "banner" ? <BannerStandings label={standings.label} entries={standings.entries} /> : null);
  const twoBeats = revealHasResult(v) && (!!result || !!standings) && (beatMs || 0) > 0;
  const full = revealMs(v) + (twoBeats ? beatMs! : 0); // the first beat comes on top of the design's own length
  const [total] = useState(() => Math.min(full, Math.max(MIN_MS, durationMs || full)));
  const [gone, setGone] = useState(false);
  const uid = useId().replace(/:/g, "");
  useEffect(() => {
    const id = window.setTimeout(() => setGone(true), total);
    return () => window.clearTimeout(id);
  }, [total]);
  if (gone) return null;

  const look = modeLook(mode);
  const royale = context === "royale";
  const upper = look.name.toUpperCase();
  // The entrance is squeezed when there is less time; the wipe away always ends
  // a beat before `total`.
  const k = Math.max(0.1, Math.min(1, (total - OUT_MS - 250) / (full - OUT_MS - 250)));
  const vars = {
    "--c": look.color,
    "--acc": royale ? "#ffd23f" : "#19f0b0",
    "--k": k,
    "--len": Math.max(7, upper.length),
    "--out-at": `${total - OUT_MS - 60}ms`,
    "--out-dur": `${OUT_MS}ms`,
    "--total": `${total}ms`,
  } as React.CSSProperties;
  // The name on two lines (stamp4): cut at the space nearest its middle.
  const cuts = upper.split("").map((ch, n) => (ch === " " ? n : -1)).filter((n) => n > 0);
  const cut = cuts.sort((a, b) => Math.abs(a - upper.length / 2) - Math.abs(b - upper.length / 2))[0];
  const stack = cut ? [upper.slice(0, cut), upper.slice(cut + 1)] : [upper];
  const label = look.line ? `Next round: ${look.name}. ${look.line}` : `Next round: ${look.name}`;

  if (v !== "slam") {
    const kick = kicker ? <div className={s.qKick}>{royale && <Crown />}{kicker}</div> : null;
    const icon = <span className={s.qIcon}><ModeIcon mode={mode} /></span>;
    const name = <div className={s.qName}>{look.name}</div>;
    const text = look.line ? <div className={s.qLine}>{look.line}</div> : null;
    const more = line ? <div className={s.qMore}>{line}</div> : null;
    return (
      <div className={`${s.root} ${royale ? s.royale : ""}`} style={vars} role="status" aria-label={label}>
        <div className={s.quiet}>
          {v === "banner" && (
            <div className={`${s.banner} ${twoBeats ? s.bnTwo : ""}`} style={{ "--beat": `${beatMs || 0}ms`, "--bar": `${barMs || 0}ms` } as React.CSSProperties}>
              {twoBeats && <div className={s.bnResult}>{result}</div>}
              <div className={s.bnSecond}><div className={s.bnMid}>
                {kick}
                {icon}
                <div className={s.bnText}>{name}{(text || more) && <div className={s.bnSub}>{text}{more}</div>}</div>
              </div></div>
              {!!barMs && <div className={s.bnBar}><i /></div>}
            </div>
          )}
          {v === "chip" && (
            <div className={s.chip}>
              {icon}
              <div className={s.chText}>{kick}{name}{text}{more}</div>
            </div>
          )}
          {v === "corner" && (
            <div className={s.corner}>
              {kick && <div className={s.cnHead}>{kick}</div>}
              <div className={s.cnBody}>{icon}<div className={s.cnText}>{name}{text}</div></div>
              {more && <div className={s.cnMore}>{more}</div>}
              <div className={s.cnBar}><i /></div>
            </div>
          )}
          {isStamp(v) && (
            <div className={`${s.stamp} ${s[v] || ""} ${v === "stamp6" ? s.stamp4 : ""}`} style={{ "--len2": Math.max(5, ...stack.map((w) => w.length)), "--n": upper.length } as React.CSSProperties}>
              <div className={s.stDim} />
              {v === "stamp3" && <span className={s.stGhost}><ModeIcon mode={mode} /></span>}
              <div className={s.stBlock}>
                <div className={s.stTop}>{icon}{kick}</div>
                {(v === "stamp" || v === "stamp6") && (
                  <>
                    {/* The outline is cut from the letters' shape (a text stroke would show
                        the font's overlapping inner contours): the name grown by 2px, minus
                        itself, over a dark fill with a soft dark halo around it. */}
                    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
                      <filter id={`o${uid}`} x="-5%" y="-30%" width="110%" height="160%" colorInterpolationFilters="sRGB">
                        <feMorphology in="SourceAlpha" operator="dilate" radius="5" />
                        <feGaussianBlur stdDeviation="7" />
                        <feComponentTransfer result="halo"><feFuncA type="linear" slope="1.5" /></feComponentTransfer>
                        <feMorphology in="SourceGraphic" operator="dilate" radius="2" result="grown" />
                        <feFlood floodColor="#070c19" floodOpacity="0.88" />
                        <feComposite in2="SourceAlpha" operator="in" result="dark" />
                        <feComponentTransfer in="SourceGraphic" result="tint"><feFuncA type="linear" slope="0.2" /></feComponentTransfer>
                        <feMerge><feMergeNode in="halo" /><feMergeNode in="grown" /><feMergeNode in="dark" /><feMergeNode in="tint" /></feMerge>
                      </filter>
                    </svg>
                    <div className={s.qName} style={{ filter: `url(#o${uid})` }}>{v === "stamp6" ? stack.map((w, n) => <span key={n}>{w}</span>) : look.name}</div>
                  </>
                )}
                {v === "stamp2" && (
                  // The outline draws itself, a letter after the other. Three layers of
                  // the same text: a dark backing, the coloured line (masked to the
                  // outside of the letters, so no inner contour shows), nothing else.
                  <svg className={s.stDraw} aria-hidden="true">
                    <mask id={`m${uid}`} maskUnits="userSpaceOnUse" x="-10%" y="-50%" width="120%" height="200%">
                      <rect x="-10%" y="-50%" width="120%" height="200%" fill="#fff" />
                      <text x="50%" y="0.95em" textAnchor="middle" fill="#000">{upper}</text>
                    </mask>
                    <text className={s.stDrawBack} x="50%" y="0.95em" textAnchor="middle">{upper}</text>
                    <text className={s.stDrawLine} x="50%" y="0.95em" textAnchor="middle" mask={`url(#m${uid})`}>
                      {upper.split("").map((ch, n) => <tspan key={n} style={{ "--i": n } as React.CSSProperties}>{ch}</tspan>)}
                    </text>
                  </svg>
                )}
                {v === "stamp3" && name}
                {v === "stamp4" && <div className={s.qName}>{stack.map((w, n) => <span key={n}>{w}</span>)}</div>}
                {v === "stamp5" && (
                  <div className={s.qName}>
                    {upper.split("").map((ch, n) => <span key={n} style={{ "--i": n } as React.CSSProperties}>{ch === " " ? "\u00a0" : ch}</span>)}
                  </div>
                )}
                {text}
                {more}
              </div>
            </div>
          )}
          {isBoard(v) && (
            <div className={`${s.board} ${s[v] || ""} ${twoBeats ? "" : s.bdAlone}`} style={{ "--beat": `${twoBeats ? beatMs : 0}ms`, "--bar": `${barMs || 0}ms` } as React.CSSProperties}>
              <div className={s.bdDim} />
              <div className={s.bdCard}>
                {twoBeats && (standings ? <BoardList standings={standings} /> : <div className={s.bdOther}>{result}</div>)}
                <div className={s.bdNextWrap}>
                  <div className={s.bdNext}>
                    {icon}
                    <div className={s.bdNextText}>{kick}{name}{text}{more}</div>
                  </div>
                </div>
                {!!barMs && <div className={s.bdBar}><i /></div>}
              </div>
            </div>
          )}
          {v === "pass" && (
            <div className={s.pass}>
              <div className={s.psPanel} />
              <div className={s.psBody}>
                {icon}
                <div className={s.psText}>
                  {kick}
                  {name}
                  {(text || more) && <div className={s.psRow}>{text}{more}</div>}
                </div>
              </div>
            </div>
          )}
          {v === "edge" && (
            <div className={s.edge}>
              <div className={s.egFrame} />
              <div className={s.egTab}>{icon}{name}</div>
              <div className={s.egUnder}>{text}<div className={s.egRow}>{kick}{more}</div></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${s.root} ${royale ? s.royale : ""}`} style={vars} role="status" aria-label={label}>
      <div className={s.slam}>
        <div className={s.lead} />
        <div className={s.panel}><span className={s.ghost}><ModeIcon mode={mode} /></span></div>
        <div className={s.flash} />
        <div className={s.body}>
          {kicker && <div className={s.kicker}>{royale && <Crown />}{kicker}</div>}
          <div className={s.name}>{upper}</div>
          {look.line && <div className={s.line}>{look.line}</div>}
          <div className={s.chips}>
            <span className={s.mapChip}>
              <MapShape shape={shape} />
              <b>{shape}</b>
              <i>· {SIZE_LABEL[sizeKey] || sizeKey} map</i>
            </span>
            {line && <span className={s.lineChip}>{royale && <Crown />}{line}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- the banner's first row: what the finished round meant ----------
// The layers build one of these and hand it to the banner as `result`.
// The standings after the round, the round's winner marked in their colour.
// `before`: the score before this round, for the bar that grows by the gain.
export interface StandingEntry { id: string; rank: number; rankBefore?: number; name: string; color: string; flagUrl?: string | null; score: number; before?: number; you?: boolean; winner?: boolean; plus?: number | null;
  // the Royale: below the stage's cut line (going out if it ends like this), or out for good
  danger?: boolean; gone?: boolean }
export interface Standings { label?: string; entries: StandingEntry[] }
interface Seat { id: string; name: string; colorId: string; flag?: string | null }
// Who the standings show: everyone when 7 or fewer play; with more, the top 5
// and my best-placed seat with its real rank.
export function standingsFor(players: Seat[], score: Record<string, number>, seatIds: string[], winnerId: string | null | undefined, winnerPoints: number | null | undefined, label?: string, scoreBefore?: Record<string, number> | null, all = false): Standings {
  const ranked = players.slice().sort((a, b) => (score[b.id] || 0) - (score[a.id] || 0)).map((p, i) => ({ p, rank: i + 1 }));
  // where everyone stood before the round (ties keep the order the room lists them in)
  const was: Record<string, number> = {};
  if (scoreBefore) players.slice().sort((a, b) => (scoreBefore[b.id] || 0) - (scoreBefore[a.id] || 0)).forEach((p, i) => { was[p.id] = i + 1; });
  let rows = ranked;
  if (ranked.length > 7 && !all) {
    rows = ranked.slice(0, 5);
    const mine = ranked.find((r) => seatIds.indexOf(r.p.id) !== -1);
    if (mine && mine.rank > 5) rows = rows.concat(mine);
  }
  return {
    label,
    entries: rows.map(({ p, rank }) => {
      const total = score[p.id] || 0, winner = p.id === winnerId;
      return {
        id: p.id, rank, rankBefore: was[p.id], name: p.name, color: (wormColors as Record<string, string>)[p.colorId] || "#8ea3bd",
        flagUrl: isFlagCode(p.flag) ? flagUrlSquare(p.flag as string) : null, score: total,
        // every player's gain when the scores before the round are known, else only the winner's
        before: scoreBefore ? Math.min(total, scoreBefore[p.id] || 0) : winner ? Math.max(0, total - (winnerPoints || 0)) : total,
        you: seatIds.indexOf(p.id) !== -1, winner,
        plus: scoreBefore ? (total - (scoreBefore[p.id] || 0) > 0 ? total - (scoreBefore[p.id] || 0) : null) : winner ? winnerPoints : null,
      };
    }),
  };
}

// The Royale's stage table for the pause: with a big field it is the top three,
// the two either side of the cut line, and me. Everyone below the line is
// marked (`danger`); once the stage is over, those who went out are (`gone`,
// and listed last among players level on points, as the server's tie-break
// decided). `cut`: how many go out when the stage ends.
export function royaleStandingsFor(players: Seat[], score: Record<string, number>, seatIds: string[], winnerId: string | null | undefined, winnerPoints: number | null | undefined, label: string | undefined, scoreBefore: Record<string, number> | null | undefined, cut: number, goneIds: string[] | null): Standings {
  const base = standingsFor(players, score, seatIds, winnerId, winnerPoints, label, scoreBefore);
  const gone = goneIds || [];
  // level on points: who went out last, then who scored more in the round just played
  const gain = (id: string) => (scoreBefore ? (score[id] || 0) - (scoreBefore[id] || 0) : 0);
  const ranked = players.slice().sort((a, b) => (score[b.id] || 0) - (score[a.id] || 0) || (gone.indexOf(a.id) === -1 ? 0 : 1) - (gone.indexOf(b.id) === -1 ? 0 : 1) || gain(b.id) - gain(a.id));
  const n = ranked.length, line = n - cut; // ranks above `line` are safe
  const rankOf = (id: string) => ranked.findIndex((p) => p.id === id) + 1;
  const full = standingsFor(players, score, seatIds, winnerId, winnerPoints, label, scoreBefore, true).entries
    .map((e) => ({ ...e, rank: rankOf(e.id), danger: goneIds ? gone.indexOf(e.id) !== -1 : rankOf(e.id) > line, gone: gone.indexOf(e.id) !== -1 }))
    .sort((a, b) => a.rank - b.rank);
  if (n <= 8) return { label: base.label, entries: full };
  const keep = new Set<number>([1, 2, 3, line, line + 1]);
  full.forEach((e) => { if (e.you) keep.add(e.rank); });
  return { label: base.label, entries: full.filter((e) => keep.has(e.rank)) };
}

// The boards' list, in the look of the game's own scoreboard: a row a player,
// the score as a bar in their colour, the round's gain as a brighter band that
// grows on. A short screen keeps the top 3 and me (the others are hidden in css).
//
// `stage` (the paced card only) plays the round's change in steps: 0 = as it
// stood before the round (old order, old places, old scores), 1 = the bars and
// the scores climb to the new ones where everyone still sits, 2 = the rows move
// to their new places. Left out, the list is simply the new standings with the
// gain growing on.
// The staged list's reorder is the game scoreboard's own (pages/play/game/Score
// swapSteps): from the old order to the new in steps, top to bottom. Each step
// lifts the row that belongs at the next place into it and shifts the rows it
// displaces along, as one move; the next step starts when that one is done.
export const ROW_MOVE_STEP_MS = 300; // a move's length at most ...
const SORT_MS = 900;                  // ... and the whole sort's, however many rows move
function swapSteps<T>(from: T[], to: T[]): T[][] {
  const steps = [from];
  let cur = from;
  for (let k = 0; k < to.length; k++) {
    if (cur[k] === to[k]) continue;
    const next = cur.filter((x) => x !== to[k]);
    next.splice(k, 0, to[k]);
    steps.push(next);
    cur = next;
  }
  return steps;
}
// `step`: how many of those moves have set off (stage 2 only); or `moveMs`, how
// long the moves have been under way, paced so the sort fits SORT_MS.
function BoardList({ standings, stage, step: stepIn = 0, moveMs, winner }: { standings: Standings; stage?: 0 | 1 | 2; step?: number; moveMs?: number; winner?: RevealWords["winner"] }) {
  const es = standings.entries;
  const max = Math.max(1, ...es.map((e) => e.score));
  const top = es.some((e) => e.you && e.rank <= 3) ? 4 : 3;
  const staged = stage != null;
  const keep = (e: StandingEntry) => e.rank <= top || !!e.you;
  // Staged, the rows are laid out in the old order and move to the new one. A
  // short screen shows only the kept rows, so their moves are worked out among
  // those (--dyk).
  const old = staged ? es.slice().sort((a, b) => (a.rankBefore ?? a.rank) - (b.rankBefore ?? b.rank) || a.rank - b.rank) : es;
  const oldKept = old.filter(keep), newKept = es.filter(keep);
  const moves = staged ? swapSteps(old, es).length - 1 : 0;
  const stepMs = Math.min(ROW_MOVE_STEP_MS, SORT_MS / Math.max(1, moves));
  const step = moveMs != null ? Math.max(0, Math.floor(moveMs / stepMs)) : stepIn;
  const at = (orders: StandingEntry[][]) => orders[Math.max(0, Math.min(orders.length - 1, stage === 2 ? step : 0))];
  const now = staged ? at(swapSteps(old, es)) : es;
  const nowKept = staged ? at(swapSteps(oldKept, newKept)) : newKept;
  return (
    <div className={`${s.bdList} ${staged ? s.bdStaged : ""} ${staged && stage! >= 1 ? s.bdUp : ""}`} style={{ "--rows": es.length, "--half": Math.ceil(es.length / 2), "--move": `${stepMs}ms` } as React.CSSProperties}>
      {winner ? (
        // the round's winner, in the card's head (the paced card)
        <div className={`${s.bdLabel} ${s.bdHead}`} style={{ "--pc": winner.color } as React.CSSProperties}>
          <span>{standings.label}</span>
          <span className={s.bdHeadWin}>Winner{winner.flagUrl ? <img src={winner.flagUrl} alt="" /> : <i />}<b>{winner.name}</b></span>
        </div>
      ) : standings.label && <div className={s.bdLabel}>{standings.label}</div>}
      {old.map((e, home) => {
        const to = (e.score / max) * 100, from = ((e.before != null ? e.before : e.score) / max) * 100;
        const dy = now.indexOf(e) - home, dyk = keep(e) ? nowKept.indexOf(e) - oldKept.indexOf(e) : 0;
        const before = staged && stage === 0;
        // its place number changes once the row is where it belongs
        const placed = !staged || (stage === 2 && now.indexOf(e) === es.indexOf(e) && (step > 0 || old.indexOf(e) === es.indexOf(e)));
        return (
          <div key={e.id} className={`${s.bdRow} ${e.winner ? s.bdWin : ""} ${e.you ? s.bdYou : ""} ${keep(e) ? s.bdKeep : ""} ${staged && placed ? s.bdPlaced : ""} ${e.danger ? s.bdDanger : ""} ${e.gone ? s.bdGone : ""}`} style={{ "--pc": e.color, "--from": `${from}%`, "--to": `${to}%`, "--dy": dy, "--dyk": dyk } as React.CSSProperties}>
            <i className={s.bdFill} /><i className={s.bdGain} /><i className={s.bdEdge} />
            {staged
              ? <span className={`${s.bdRank} ${s.bdRanks}`}><i>{e.rankBefore ?? e.rank}</i><i>{e.rank}</i></span>
              : <span className={s.bdRank}>{e.rank}</span>}
            {e.flagUrl ? <img className={s.bdFlag} src={e.flagUrl} alt="" /> : <u className={s.bdFlag} />}
            <b className={s.bdName}>{e.name}</b>
            {!!e.plus && e.plus > 0 && <strong className={s.bdPlus}>+{e.plus}</strong>}
            <em className={s.bdScore}>{before && e.before != null ? e.before : e.score}</em>
          </div>
        );
      })}
    </div>
  );
}
export function BannerStandings({ label, entries }: { label?: string; entries: StandingEntry[] }) {
  return (
    <div className={s.bsRow}>
      {label && <span className={s.bwTakes}>{label}</span>}
      {entries.map((e) => (
        <span key={e.id} className={`${s.bsEntry} ${e.winner ? s.bsWin : ""} ${e.you ? s.bsYou : ""}`} style={{ "--pc": e.color } as React.CSSProperties}>
          <i>{e.rank}</i>
          {e.flagUrl ? <img src={e.flagUrl} alt="" /> : <u />}
          <b>{e.name}</b>
          <em>{e.score}</em>
          {e.winner && !!e.plus && e.plus > 0 && <strong>+{e.plus}</strong>}
        </span>
      ))}
    </div>
  );
}
export function BannerOut({ alive, out }: { alive?: number | null; out: { id: string; name: string; flagUrl?: string | null; place?: number | null }[] }) {
  return (
    <div className={s.bwRow}>
      {alive != null && <b className={s.boLeft}>{alive}<small>left</small></b>}
      {out.length > 0 && <span className={s.bwTakes}>Out</span>}
      <span className={s.boList}>
        {out.map((p) => (
          <span key={p.id} className={s.boChip}>
            {p.flagUrl && <img src={p.flagUrl} alt="" />}<u>{p.name}</u>{p.place != null && <em>{ordinal(p.place)}</em>}
          </span>
        ))}
      </span>
    </div>
  );
}

// ---------- the paced card (board1, what a real game shows) ----------
// The pause between rounds, paced by the clock instead of one CSS timeline:
//   1. a blink after the crash, the standings pop in, the round's winner in
//      their head (how the round went, every gain);
//   2. the next round, under them in the same card, comes and goes with them
//      (the card fades as one, with the old round's trails and heads);
//   3. at the latest it all fades when the countdown shows its "3".
// After a set (the podium said how it went) only the next round shows.
const PACED = PICKED === "board1";
const BREATH_MS = 150;       // 1: before the standings
const ALONE_LEAD_MS = 3400;  // 2: the next round, when it is the pause's only word (after a set), appears this long before the round changes
const COUNT_FROM = 3;        // 4: the countdown's first number
const ROWS_LINGER_MS = 0;    //    (after a set) the standings stay this long into the new round
// The standings play the round's change in steps (BoardList's stages), counted
// from the moment they appear: as it stood, then the scores climb, then the rows
// move, one at a time.
// (the climb waits a frame so its bars grow from where they stood; it takes 0.8s)
const CLIMB_AFTER_MS = 120;
const CLIMB_MS = 800;        // the bars' climb (the stylesheet's .bdStaged transition)
const MOVE_AFTER_MS = CLIMB_AFTER_MS + CLIMB_MS; // the sort starts as the bars are full
// After a set (what.setEnd): the set's winner takes the next round's place under
// the standings (and appears with them), then the whole card turns over into the podium (the layer's own
// card, which waits for SET_FLIP_MS), and the next round comes last, alone.
export const SET_FLIP_MS = 3500;  // into the pause
const FLIP_MS = 420;
const LINGER_MAX_MS = 9000;  //    never longer than this into the new round
function PacedBoard({ at, ends, what, getGameState, onDone }: { at: number; ends: number; what: RevealWords; getGameState?: () => any; onDone: () => void }) {
  const hasResult = !!(what.standings || what.result);
  const rowsAt = at + BREATH_MS;
  const setEnd = hasResult ? what.setEnd : null;
  const flipAt = at + SET_FLIP_MS;
  // (after a round the next round comes with the standings, as one card)
  const nextAt = setEnd ? Math.max(flipAt + 1500, ends - ALONE_LEAD_MS) : hasResult ? rowsAt : Math.max(at, ends - ALONE_LEAD_MS);
  const [now, setNow] = useState(() => Date.now());
  const [leaving, setLeaving] = useState(false);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  // (read through a ref: the game hands a new function on every render, and an
  // effect keyed on it restarted the clock each time, so in a busy room, the
  // Royale's, it hardly ticked and the card came up seconds late)
  const gsRef = useRef(getGameState);
  gsRef.current = getGameState;
  useEffect(() => {
    const id = window.setInterval(() => {
      const t = Date.now();
      setNow(t);
      const getGs = gsRef.current;
      const gs = getGs ? getGs() : null;
      if (t < ends + 250) return;
      // The new round is on: stay through its roll call, go at the countdown's "3".
      const left = gs && typeof gs.startPhaseTime === "number" && typeof gs.gameTime === "number" && gs.gameTime <= gs.startPhaseTime ? gs.startPhaseTime - gs.gameTime : null;
      if ((left != null && left <= COUNT_FROM + 0.05) || t > ends + LINGER_MAX_MS || (!getGs && t > ends + 600)) setLeaving(true);
    }, 100);
    return () => window.clearInterval(id);
  }, [ends]);
  useEffect(() => {
    if (!leaving) return;
    const id = window.setTimeout(() => doneRef.current(), 1200); // the fade away (the stylesheet's 1.1s) plays out first
    return () => window.clearTimeout(id);
  }, [leaving]);

  const look = modeLook(what.mode);
  const royale = what.context === "royale";
  const rowsEnd = setEnd ? flipAt : ends + ROWS_LINGER_MS;
  // After a round (not a set) the standings and the next round are one card,
  // and it fades away as one (.pcGone on the wrap), never part by part.
  const together = hasResult && !setEnd;
  // (it fades with the old round's trails and heads: GameCanvas contentFade, the same timing)
  const fade = contentFadeFor((ends - at) / 1000);
  const gone = together && (now >= at + fade.at * 1000 || leaving);
  const rowsOn = hasResult && now >= rowsAt && (together || (now < rowsEnd && !leaving));
  const stage: 0 | 1 | 2 = now >= rowsAt + MOVE_AFTER_MS ? 2 : now >= rowsAt + CLIMB_AFTER_MS ? 1 : 0;
  const rowsShown = hasResult && now >= rowsAt;
  // Who won the round: in the standings' head; on its own only when there are none.
  const winOn = !!what.winner && !what.standings && now < rowsEnd && !leaving;
  // after a set the slot under the standings first holds the set's winner
  const setWinOn = !!setEnd && !!setEnd.winner && now >= rowsAt && now < flipAt;
  const showSetWin = !!setEnd && !!setEnd.winner && now < flipAt + 600;
  const flipping = !!setEnd && now >= flipAt && now < flipAt + 900;
  // (after a set the podium stays to the end of the pause: no next round alone after it)
  const nextOn = setWinOn || (!setEnd && now >= nextAt && (together || !leaving));
  // The podium's sound as a set's card turns over. (No reveal whoosh: the card
  // comes up with the round-over chime, RemoteGame, and its low drop a beat
  // later read as a thump.)
  useSoundOn(flipping, "podium");
  const vars = { "--c": look.color, "--acc": royale ? "#ffd23f" : "#19f0b0" } as React.CSSProperties;
  return (
    <div className={s.root} style={vars} role="status" aria-label={look.line ? `Next round: ${look.name}. ${look.line}` : `Next round: ${look.name}`}>
      <div className={s.quiet}>
        <div className={`${s.board} ${s.board1}`}>
          {/* (after a round the arena's own dim, the winner's spotlight, does this: none over it) */}
          {!together && <div className={`${s.pcDim} ${rowsOn && !gone ? s.pcOn : ""}`} />}
          {what.winner && !what.standings && (
            <div className={`${s.pcWin} ${winOn ? s.pcOn : ""}`} style={{ "--pc": what.winner.color } as React.CSSProperties}>
              <span>Round winner</span>
              {what.winner.flagUrl ? <img src={what.winner.flagUrl} alt="" /> : <i />}
              <b>{what.winner.name}</b>
            </div>
          )}
          <div className={`${s.pcWrap} ${flipping ? s.pcFlip : ""} ${gone ? s.pcGone : ""}`} style={gone ? { transitionDuration: `${fade.duration}s, 0.42s` } : undefined}>
            {hasResult && (
              // (hidden until its moment, but always laid out: the space is the rows')
              <div className={`${s.pcRows} ${rowsOn ? s.pcOn : ""}`}>
                <div key={rowsShown ? "on" : "off"} style={rowsShown ? undefined : { visibility: "hidden" }}>
                  {what.standings ? <BoardList standings={what.standings} winner={what.winner} stage={stage} moveMs={now - (rowsAt + MOVE_AFTER_MS)} /> : <div className={s.bdOther}>{what.result}</div>}
                </div>
              </div>
            )}
            {showSetWin ? (
              // the set's winner, where the next round usually is
              <div className={`${s.pcNext} ${s.pcSetWin} ${nextOn ? s.pcOn : ""}`} style={{ "--c": setEnd!.winner!.color } as React.CSSProperties}>
                {setEnd!.winner!.flagUrl ? <img className={s.pcSetFace} src={setEnd!.winner!.flagUrl} alt="" /> : <i className={s.pcSetFace} />}
                <div className={s.bdNextText}>
                  <div className={s.qKick}>Match winner</div>
                  <div className={s.qName}>{setEnd!.winner!.name}</div>
                </div>
              </div>
            ) : (
              <div className={`${s.pcNext} ${nextOn ? s.pcOn : ""} ${!hasResult || (!together && now >= rowsEnd) ? s.pcAlone : ""} ${royale && together ? s.pcNextLate : ""}`}>
                <span className={s.qIcon}><ModeIcon mode={what.mode} /></span>
                <div className={s.bdNextText}>
                  {what.kicker ? <div className={s.qKick}>{royale && <Crown />}{what.kicker}</div> : null}
                  <div className={s.qName}>{look.name}</div>
                  {look.line ? <div className={s.qLine}>{look.line}</div> : null}
                  {what.line ? <div className={s.qMore}>{what.line}</div> : null}
                </div>
                {/* what kind of round the next one is (the Royale's Knockout round, its final) */}
                {what.badge ? <span className={s.pcBadge}>{what.badge}</span> : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// A straddling reveal (the pass-through, the banner) cannot live inside the
// pause's screens: it is still there when the next round has begun. So the
// layer calls this at its top; it starts the reveal by the clock and keeps it
// whatever the phase does. `what` is read when the reveal starts and held from
// then on, so its words do not change when the round does.
// The banner is the pause's one band: given a `result` it comes in as the pause
// starts, says that first, and turns to the next round for its usual last part.
export interface RevealWords { mode: string | undefined | null; context: DynamicKind; kicker?: string; line?: string; badge?: React.ReactNode; result?: React.ReactNode; standings?: Standings; winner?: { name: string; color: string; flagUrl?: string | null } | null;
  // the round closed a set: who won it
  setEnd?: { winner: { name: string; color: string; flagUrl?: string | null } | null } | null }
export function useStraddlingReveal(paused: boolean, pauseStartedAt: number, pauseMs: number, what: RevealWords | null, getGameState?: () => any): React.ReactElement | null {
  const [shown, setShown] = useState<{ at: number; ms: number; lead: number; what: RevealWords } | null>(null);
  const whatRef = useRef(what);
  whatRef.current = what;
  const on = revealStraddles(PICKED) && !PACED;
  const ready = !!(what && what.mode);
  // The paced card (board1, the default) keeps its own time: see PacedBoard.
  const [paced, setPaced] = useState<{ at: number; ends: number; what: RevealWords } | null>(null);
  useEffect(() => {
    if (!PACED || !paused || !ready) return;
    setPaced((cur) => (cur && cur.at === pauseStartedAt ? cur : { at: pauseStartedAt, ends: pauseStartedAt + pauseMs, what: whatRef.current as RevealWords }));
  }, [paused, ready, pauseStartedAt, pauseMs]);
  const early = revealHasResult(PICKED) && !!(what && (what.result || what.standings));
  useEffect(() => {
    if (!on || !paused || !ready) return;
    const ends = pauseStartedAt + pauseMs;
    const id = window.setTimeout(() => {
      const w = whatRef.current;
      const lead = ends - Date.now();
      if (!w || !w.mode || lead < 300) return; // too late for it: skip
      setShown((cur) => (cur && cur.at === pauseStartedAt ? cur : { at: pauseStartedAt, ms: lead + revealMs(PICKED) - revealLeadMs(PICKED), lead, what: w }));
    }, early ? 0 : Math.max(0, ends - revealLeadMs(PICKED) - Date.now()));
    return () => window.clearTimeout(id);
  }, [on, paused, ready, early, pauseStartedAt, pauseMs]);
  if (PACED) {
    if (!paced) return null;
    const at = paced.at;
    return <PacedBoard key={at} at={at} ends={paced.ends} what={paced.what} getGameState={getGameState} onDone={() => setPaced((cur) => (cur && cur.at === at ? null : cur))} />;
  }
  if (!shown) return null;
  const w = shown.what;
  const beatMs = shown.lead - revealLeadMs(PICKED); // the first beat's time: what is left before the usual start
  return (
    <NextRoundReveal
      key={shown.at} variant={PICKED} mode={w.mode as string} shape="Stadium" sizeKey="medium" context={w.context} kicker={w.kicker} line={w.line}
      durationMs={shown.ms} barMs={revealHasResult(PICKED) ? shown.lead : undefined}
      result={revealHasResult(PICKED) && beatMs > 600 ? w.result : undefined} standings={revealHasResult(PICKED) && beatMs > 600 ? w.standings : undefined} beatMs={beatMs}
    />
  );
}
